"use client";

import { CalendarClock, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ThemedSelect } from "@/components/ui-system/select";
import { useOnClickOutside } from "@/hooks/use-on-click-outside";
import { useTimeRangeStore } from "@/store/time-range";

import { cn } from "@/lib/utils/cn";

const PRESETS = [
  { id: "1h" as const, label: "1h", description: "Last hour" },
  { id: "24h" as const, label: "24h", description: "Last day" },
  { id: "7d" as const, label: "7d", description: "Last 7 days" },
  { id: "custom" as const, label: "Custom", description: "Choose from and to" },
];

/**
 * Compact time range (presets + from/to) for the page tool row / `PageHeader` actions cluster.
 */
export function InlineTimeRangeControls({
  className,
  showLabel = true,
  bare = false,
}: {
  className?: string;
  showLabel?: boolean;
  /** Renders a single select control for dense toolbars such as Explorer. */
  bare?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const preset = useTimeRangeStore((s) => s.preset);
  const setPreset = useTimeRangeStore((s) => s.setPreset);
  const start = useTimeRangeStore((s) => s.start);
  const end = useTimeRangeStore((s) => s.end);
  const setRange = useTimeRangeStore((s) => s.setRange);
  const [isCustomEditorOpen, setIsCustomEditorOpen] = useState(false);

  const label = useMemo(() => {
    if (preset === "custom") {
      return "Custom";
    }
    return PRESETS.find((item) => item.id === preset)?.label ?? "Range";
  }, [preset]);

  const closeCustomEditor = useCallback(() => setIsCustomEditorOpen(false), []);
  useOnClickOutside(rootRef, closeCustomEditor, isCustomEditorOpen);

  useEffect(() => {
    if (preset === "custom") {
      setIsCustomEditorOpen(true);
      return;
    }
    setIsCustomEditorOpen(false);
  }, [preset]);

  const rangeSelect = (
    triggerClassName: string,
    menuClassName: string,
  ) => (
    <ThemedSelect
      value={preset}
      ariaLabel="Range"
      triggerClassName={triggerClassName}
      menuClassName={menuClassName}
      optionClassName="rounded-xl"
      options={PRESETS.map((option) => ({
        value: option.id,
        label: option.label,
        description: option.description,
      }))}
      onChange={(nextValue) => {
        const nextPreset = nextValue as (typeof PRESETS)[number]["id"];
        setPreset(nextPreset);
        setIsCustomEditorOpen(nextPreset === "custom");
      }}
    />
  );

  return (
    <div
      ref={rootRef}
      className={cn("relative flex flex-col gap-1", bare ? "min-w-[8rem]" : "min-w-[12rem]", className)}
    >
      {showLabel ? <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Range</span> : null}
      {bare ? (
        <label className="block w-[8rem]">
          <span className="sr-only">Range</span>
          {rangeSelect(
            "h-9 min-h-0 w-full rounded-control border border-card-border bg-card px-3 py-0 text-caption font-medium shadow-none hover:border-primary/25 focus-visible:ring-primary/20",
            "min-w-[10rem] rounded-2xl border border-card-border p-ds-2 shadow-card",
          )}
        </label>
      ) : (
        <div className="inline-flex min-h-11 min-w-[12rem] items-center gap-2 rounded-[20px] border border-card-border bg-card p-1 shadow-card">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[16px] border border-primary/20 bg-primary/10 text-primary">
            <CalendarClock className="h-4 w-4" aria-hidden />
          </span>
          <label className="min-w-0 flex-1">
            <span className="sr-only">Range</span>
            {rangeSelect(
              "h-9 min-h-0 rounded-xl border border-card-border bg-muted-bg/40 px-3 py-0 text-caption font-medium shadow-none hover:border-primary/25 focus-visible:ring-primary/20",
              "min-w-[12rem] rounded-2xl border border-card-border p-ds-2 shadow-card",
            )}
          </label>
        </div>
      )}

      {preset === "custom" && isCustomEditorOpen ? (
        <div className="absolute right-0 top-full z-50 mt-3 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-card-border bg-card p-ds-3 shadow-card">
          <div className="grid gap-ds-3 rounded-xl border border-card-border bg-muted-bg/30 p-ds-3">
            <div className="flex items-start justify-between gap-3">
              <p className="text-micro font-semibold tracking-[0.04em] text-muted">Custom range selected for {label}</p>
              <button
                type="button"
                onClick={closeCustomEditor}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted transition-colors hover:bg-muted-bg hover:text-foreground"
                aria-label="Close custom range editor"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <label className="grid gap-1.5">
              <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">From</span>
              <input
                type="datetime-local"
                value={toLocalInput(start)}
                onChange={(e) => {
                  const s = new Date(e.target.value);
                  if (!Number.isNaN(s.getTime())) {
                    setRange(s, end);
                  }
                }}
                className="ui-control min-h-11 rounded-xl text-caption leading-[1.25]"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">To</span>
              <input
                type="datetime-local"
                value={toLocalInput(end)}
                onChange={(e) => {
                  const en = new Date(e.target.value);
                  if (!Number.isNaN(en.getTime())) {
                    setRange(start, en);
                  }
                }}
                className="ui-control min-h-11 rounded-xl text-caption leading-[1.25]"
              />
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeCustomEditor}
                className="btn btn-secondary btn-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SegmentedInlineTimeRangeControls({ className }: { className?: string }) {
  const preset = useTimeRangeStore((s) => s.preset);
  const setPreset = useTimeRangeStore((s) => s.setPreset);
  const start = useTimeRangeStore((s) => s.start);
  const end = useTimeRangeStore((s) => s.end);
  const setRange = useTimeRangeStore((s) => s.setRange);

  return (
    <div className={cn("flex flex-wrap items-center gap-x-ds-2 gap-y-1", className)}>
      <span className=" text-[0.625rem] uppercase tracking-[0.12em] text-kicker-lux">Range</span>
      <div className="flex gap-1">
        {PRESETS.filter((p) => p.id !== "custom").map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPreset(p.id)}
            className={cn(
              "rounded-md px-ds-2 py-0.5  text-[0.6875rem] transition-[color,background-color,box-shadow] duration-200",
              preset === p.id
                ? "bg-accent-dim text-foreground shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--accent)_35%,transparent),0_1px_8px_-2px_color-mix(in_srgb,var(--accent)_25%,transparent)]"
                : "text-muted hover:bg-muted-bg/55 hover:text-foreground",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-1  text-[0.625rem] text-muted">
        <span>From</span>
        <input
          type="datetime-local"
          value={toLocalInput(start)}
          onChange={(e) => {
            const s = new Date(e.target.value);
            if (!Number.isNaN(s.getTime())) {
              setRange(s, end);
            }
          }}
          className="ui-control max-w-[10.5rem] py-0.5 text-[0.6875rem]"
        />
      </label>
      <label className="flex items-center gap-1  text-[0.625rem] text-muted">
        <span>To</span>
        <input
          type="datetime-local"
          value={toLocalInput(end)}
          onChange={(e) => {
            const en = new Date(e.target.value);
            if (!Number.isNaN(en.getTime())) {
              setRange(start, en);
            }
          }}
          className="ui-control max-w-[10.5rem] py-0.5 text-[0.6875rem]"
        />
      </label>
    </div>
  );
}

/** Legacy full-bleed strip under the app header (prefer `InlineTimeRangeControls` in `PageHeader`). */
export function GlobalTimeRangeBar({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-ds-2 border-b border-border/50 bg-card/25 px-ds-4 py-ds-2 backdrop-blur-md supports-[backdrop-filter]:bg-card/15 dark:bg-card/20",
        className,
      )}
    >
      <InlineTimeRangeControls />
    </div>
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
